import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById, getComicsByCategory } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">漫画不存在</h1>
            <p className="text-foreground-600 mb-4">该漫画可能已被下架或链接有误</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = getComicsByCategory(comic.category).filter((c) => c.id !== comic.id).slice(0, 4);
  const categoryNames: Record<string, string> = {
    rexue: '热血', lianai: '恋爱', xuanhuan: '玄幻', gaoxiao: '搞笑',
    xuanyi: '悬疑', dushi: '都市', kehuan: '科幻', hanman: '韩漫',
  };
  const categoryName = categoryNames[comic.category] || comic.category;

  return (
    <>
      <title>{comic.title} - 非常爱漫画免费在线看全集 | 爱漫画</title>
      <meta name="description" content={`${comic.title}免费在线看全集，${comic.description.substring(0, 120)}。非常爱漫画提供下拉式阅读体验。`} />
      <meta name="keywords" content={`${comic.title}, ${comic.title}免费, ${comic.title}在线看, ${comic.title}全集, ${categoryName}漫画, 爱漫画, 非常爱漫画`} />
      <link rel="canonical" href={`https://www.aimanhua.com/comic/${comic.id}`} />
      <meta property="og:title" content={`${comic.title} - 非常爱漫画免费在线看全集 | 爱漫画`} />
      <meta property="og:description" content={`${comic.title}免费在线看全集，${comic.description.substring(0, 120)}。非常爱漫画提供下拉式阅读体验。`} />
      <meta property="og:url" content={`https://www.aimanhua.com/comic/${comic.id}`} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={comic.coverImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${comic.title} - 非常爱漫画免费在线看全集`,
          "description": comic.description,
          "image": comic.coverImage,
          "author": {
            "@type": "Person",
            "name": comic.author
          },
          "datePublished": "2024-01-01",
          "dateModified": comic.updateDate,
          "publisher": {
            "@type": "Organization",
            "name": "爱漫画",
            "url": "https://www.aimanhua.com"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.aimanhua.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": `${categoryName}漫画`,
              "item": `https://www.aimanhua.com/category/${comic.category}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": comic.title,
              "item": `https://www.aimanhua.com/comic/${comic.id}`
            }
          ]
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span>/</span>
                <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {categoryName}漫画
                </Link>
                <span>/</span>
                <span className="text-foreground-800 font-medium">{comic.title}</span>
              </nav>
            </div>
          </div>

          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0">
                <div className="w-full sm:w-60 lg:w-72 aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 非常爱漫画免费在线看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                    {categoryName}
                  </span>
                  <span className="text-sm text-foreground-500">
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </div>
                    {comic.rating} 分
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-lg bg-background-100 border border-background-200/70 text-xs text-foreground-600 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500 mb-6">
                  <span>
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-eye-line"></i>
                    </div>
                    {(comic.views / 10000).toFixed(0)}万 阅读
                  </span>
                  <span>
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-file-list-3-line"></i>
                    </div>
                    共 {comic.chapters.length} 话
                  </span>
                  <span>
                    <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-time-line"></i>
                    </div>
                    更新于 {comic.updateDate}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-play-circle-line"></i>
                      </div>
                      开始阅读
                    </span>
                  </button>
                  <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg border border-background-200/70 transition-all duration-300 whitespace-nowrap cursor-pointer">
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-bookmark-line"></i>
                      </div>
                      收藏漫画
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                章节目录
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
                {comic.chapters.slice(0, 30).map((chapter) => (
                  <button
                    key={chapter.id}
                    className="px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 transition-all duration-200 text-left whitespace-nowrap cursor-pointer"
                  >
                    {chapter.title}
                  </button>
                ))}
              </div>
              {comic.chapters.length > 30 && (
                <button className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                  查看全部 {comic.chapters.length} 话
                  <div className="w-4 h-4 inline-flex items-center justify-center ml-1">
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                </button>
              )}
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-12 md:mt-16">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/comic/${rel.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          title={`${rel.title} - 非常爱漫画免费在线看`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-primary-500 text-white text-xs font-medium">
                          {rel.rating}
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{rel.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}